import { isGroupRow } from "./is-group-row"

interface ContributorMetricValues {
  description?: string | null
}

export interface MatchableContributor {
  id: string
  label?: string
  children?: Record<string, unknown>
  description?: string | null
  values?: Record<string, ContributorMetricValues | undefined>
}

export interface TableRowMatch {
  id: string
  label: string
  description: string
}

/** True when every row visible at the current table level is a group node rather than a security. */
export function isCurrentLevelGrouped(sortedContributorsArray: MatchableContributor[]): boolean {
  return sortedContributorsArray.length > 0 && isGroupRow(sortedContributorsArray[0])
}

// The security's full name lives on `description` — flat on some rows, nested under a metric
// prefix in `values` on others, depending on which gatherer populated this table level.
function getContributorDescription(contributor: MatchableContributor): string {
  if (contributor.description) return contributor.description
  const values = Object.values(contributor.values ?? {})
  return values.find(value => value?.description)?.description ?? ""
}

function rowMatchesQuery(contributor: MatchableContributor, normalizedQuery: string): boolean {
  const label = String(contributor.label ?? "").toLowerCase()
  const ticker = String(contributor.id ?? "").toLowerCase()
  const description = getContributorDescription(contributor).toLowerCase()
  return label.includes(normalizedQuery) || ticker.includes(normalizedQuery) || description.includes(normalizedQuery)
}

/** Every current-level row as a match candidate, unfiltered — the option set for the finder. */
export function toTableRowMatches(sortedContributorsArray: MatchableContributor[]): TableRowMatch[] {
  return sortedContributorsArray.map(contributor => ({
    id: contributor.id,
    label: contributor.label ?? contributor.id,
    description: getContributorDescription(contributor),
  }))
}

/**
 * Matches the current table's own rows against a query — never the broader security universe.
 * When the current level is grouped, only group rows are eligible; individual securities inside
 * a group are out of scope until the grid supports revealing a nested row without losing context.
 */
export function matchTableRows(sortedContributorsArray: MatchableContributor[], query: string): TableRowMatch[] {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return []

  const isGrouped = isCurrentLevelGrouped(sortedContributorsArray)

  return sortedContributorsArray
    .filter(contributor => !isGrouped || isGroupRow(contributor))
    .filter(contributor => rowMatchesQuery(contributor, normalizedQuery))
    .map(contributor => ({
      id: contributor.id,
      label: contributor.label ?? contributor.id,
      description: getContributorDescription(contributor),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}
